import React, { useState, useEffect } from "react";
import api, { ApiResponse } from "../../services/api";
import { RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { activeCurrencySymbol } from "../../utils/currency";
interface OrderItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  discountAmount: number;
  customOptions?: { label: string; price: number; qty: number }[];
  itemstatus?: string;
  tax?: number;
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  customerId: string;
  createdAt: string;
  phoneNumber?: string;
  tableToken?: number;
  orderBy: string;
  employeeName?: string;
  discountAmount: number;
  choice?: string;
  payChoice?: string;
  scheduledAt?: Date;
}

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchPhoneNumber, setSearchPhoneNumber] = useState<string>("");
  const [PhoneNumber, setPhoneNumber] = useState("");
  const [totalOrders, setTotalOrders] = useState(0); // Total number of orders
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const ordersPerPage = 10;
  const totalPages = Math.ceil(totalOrders / ordersPerPage); // Calculate total pages

  useEffect(() => {
    if (!isSearchMode) {
      fetchOrders();
    }
  }, [currentPage]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get<ApiResponse<Order[]>>(
        `/admin/orders?page=${currentPage}&limit=${ordersPerPage}`,
      );
      if (response.data.success) {
        setOrders(response.data.data); // Update the orders data
        setTotalOrders(response.data.total || 0); // Ensure fallback to 0 if total is undefined
      }
    } catch {
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersByPhoneNumber = async (phoneNumber: string) => {
    try {
      setLoading(true);
      setIsSearchMode(true);
      setCurrentPage(1);
      const cleanPhoneNumber = PhoneNumber.replace(/-/g, "");
      const response = await api.get<ApiResponse<Order[]>>(
        `/admin/orders/fetch/${cleanPhoneNumber}`,
      );
      setOrders(response.data.success ? response.data.data : []);
    } catch (err) {
      console.error("Error fetching orders by phone number:", err);
      toast.error("Failed to fetch orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove all non-digit and non-dash characters
    const cleaned = input.replace(/[^\d-]/g, "");

    // Extract just the digits for validation and API call
    const digits = cleaned.replace(/-/g, "").slice(0, 10); // Only 10 digits max

    let formatted = "";

    // Check if user manually typed dashes in correct positions only
    const validManualFormat =
      /^\d{1,3}$|^\d{3}-\d{0,3}$|^\d{3}-\d{3}-\d{0,4}$/.test(cleaned);

    if (validManualFormat && cleaned === input.replace(/[^\d-]/g, "")) {
      // User typed valid format with dashes in correct positions, keep it
      formatted = cleaned;
    } else {
      // Auto-format based on digit count
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formatted = digits;
      }
    }

    setPhoneNumber(formatted);
  };
  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!PhoneNumber.trim()) {
      return;
    }
    try {
      fetchOrdersByPhoneNumber(PhoneNumber);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="orders-container max-w-7xl mx-auto h-screen p-4 pt-0 lg:p-8 flex flex-col overscroll-y-auto no-scrollbar relative">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm   py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search by Phone Number
          </label>

          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="text"
              value={PhoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Enter phone number"
              className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <button
              type="submit"
              className="px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Search
            </button>

            <button
              onClick={() => {
                setPhoneNumber("");
                setIsSearchMode(false);
                setCurrentPage(1);
                fetchOrders();
              }}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Orders"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </form>
        </div>
      </div>
      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1 ">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              Loading orders...
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No orders found</p>
              <p className="text-sm mt-1">
                Try searching with another phone number
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {orders.map((order) => (
              <div
                key={order._id}
                className="bg-white border rounded-lg p-3 shadow-sm hover:shadow transition"
              >
                {/* Order Top */}
                <div className="flex justify-between items-start mb-1.5">
                  <div>
                    <h3 className="text-blue-600 font-semibold text-sm">
                      Order #{order.orderNumber}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                    {order.scheduledAt && (
                      <p className="text-xs text-gray-500">
                        Scheduled:{" "}
                        {new Date(order.scheduledAt).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="font-semibold text-sm text-blue-600">
                    {order.totalAmount === 0 ? (
                      <span className="text-red-500 text-xs">Cancelled</span>
                    ) : (
                      `${activeCurrencySymbol()}${order.totalAmount.toFixed(2)}`
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div className="text-xs text-gray-700 space-y-0.5 mb-2">
                  {order.payChoice && <p>Payment: {order.payChoice}</p>}
                  {order.phoneNumber && (
                    <p className="mb-1">📞 {order.phoneNumber}</p>
                  )}
                  {order.choice && <p>Choice: {order.choice}</p>}
                  {order.tableToken && <p>Table: {order.tableToken}</p>}
                  {!order.phoneNumber && !order.tableToken && (
                    <p className="text-yellow-600">⚠️ No contact</p>
                  )}
                </div>

                {/* Items */}
                <div>
                  <h4 className="font-medium text-xs mb-1">Items</h4>
                  <div className="max-h-36 overflow-y-auto pr-1">
                    {order.items.map((item, i) => (
                      <div
                        key={i}
                        className="flex justify-between bg-gray-100 rounded px-2 py-1 mb-1 text-xs"
                      >
                        <span>
                          {item.name} ×{item.quantity}
                        </span>
                        <span>{activeCurrencySymbol()}{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {!isSearchMode && orders.length > 0 && (
        <div className="mt-4 flex justify-center">
          <div className="flex flex-wrap justify-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="rounded bg-gray-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`rounded px-2.5 py-1 text-xs sm:px-3 sm:text-sm ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="rounded bg-gray-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
